// src/App.js
import React, { useEffect, useState, Suspense, lazy } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { currentUser } from './functions/auth';
import { auth } from './firebase';

// ---- Auth pages (eager where small / frequently hit) ----
// LEGACY - commented out for migration testing
// import Register from './pages/auth/Register';
// import Login from './pages/auth/Login';
// import ForgotPassword from './pages/auth/ForgotPassword';
// import UserManagement from './pages/auth/UserManagement';

import { HeaderProvider } from "./components/layout/HeaderContext";
// import MainLayout from "./components/layout/MainLayout";
import RouteLoadingBar, { LoadingProvider, LoadingTrigger } from "./components/layout/RouteLoadingBar";
import { BuyerLocationProvider } from "./contexts/BuyerLocationContext";
import InstallPWA from "./components/pwa/InstallPWA";
import { AgreementChecker } from "./components/agreements";

// ---- Route guards ----
const UserRoute   = lazy(() => import('./components/routes/UserRoute'));

// ---- Auth pages (lazy) ----
const LoginModern = lazy(() => import('./pages/auth/LoginModern'));
const RegisterModern = lazy(() => import('./pages/auth/RegisterModern'));
const UserManagementModern = lazy(() => import('./pages/auth/UserManagementModern'));
const ForgotPasswordModern = lazy(() => import('./pages/auth/ForgotPasswordModern'));
const AdminRoute  = lazy(() => import('./components/routes/AdminRoute'));
const SellerRoute = lazy(() => import('./components/routes/SellerRoute'));

// ---- Seller (lazy) ----
const SellerPublicProductList   = lazy(() => import('./pages/seller/product/SellerPublicProductList'));
const SellerProductListModern   = lazy(() => import('./pages/seller/product/SellerProductListModern'));
const SellerProductCreate       = lazy(() => import('./pages/seller/product/SellerProductCreate'));
const SellerProductUpdate       = lazy(() => import('./pages/seller/product/SellerProductUpdate'));
// const SellerPodcasts            = lazy(() => import('./pages/seller/video/SellerPodcasts'));
const SellerPodcastsModern      = lazy(() => import('./pages/seller/video/SellerPodcastsModern'));
// const SellerPublicVideoPage     = lazy(() => import('./pages/seller/video/SellerPublicVideoPage')); // Not used in any route
const SellerPodcast             = lazy(() => import('./pages/seller/video/SellerPodcast'));
const SellerHomePage            = lazy(() => import('./pages/seller/SellerHomePage'));
const SellerHomeBuilderPage     = lazy(() => import('./pages/seller/SellerHomeBuilderPage'));
const SellerHomePreviewPage     = lazy(() => import('./pages/seller/SellerHomePreviewPage'));
// const SellerPodcastCreate       = lazy(() => import('./pages/seller/video/SellerPodcastCreate'));
const SellerPodcastCreateModern = lazy(() => import('./pages/seller/video/SellerPodcastCreateModern'));
const SellerPodcastUpdate       = lazy(() => import('./pages/seller/video/SellerPodcastUpdate'));
// const SellerOrderList           = lazy(() => import('./pages/seller/order/SellerOrderList'));
const SellerOrderListModern     = lazy(() => import('./pages/seller/order/SellerOrderListModern'));
// const SellerDashboard           = lazy(() => import('./pages/seller/SellerDashboard'));
const SellerDashboardModern     = lazy(() => import('./pages/seller/SellerDashboardModern'));
const SellerPressRelease        = lazy(() => import('./pages/seller/SellerPressRelease'));
// const SellerOrderDetails        = lazy(() => import('./pages/seller/order/SellerOrderDetails'));
const SellerOrderDetailsModern  = lazy(() => import('./pages/seller/order/SellerOrderDetailsModern'));
// const SellerCreatePressReleaseAndCoupon = lazy(() => import('./pages/seller/SellerCreatePressReleaseAndCoupon'));
const SellerPressReleaseCouponModern = lazy(() => import('./pages/seller/SellerPressReleaseCouponModern'));
const SellerCustomersPictures   = lazy(() => import('./pages/seller/SellerCustomersPictures'));
const SellerProfile             = lazy(() => import('./pages/seller/SellerProfile'));
const SellerProfilePage         = lazy(() => import('./pages/seller/SellerProfilePage'));
const SellerVerificationPage    = lazy(() => import('./pages/seller/SellerVerificationPage'));
const SellerLocationSettings    = lazy(() => import('./pages/seller/SellerLocationSettings'));
const NewBusinessPage           = lazy(() => import('./pages/seller/NewBusinessPage'));
const TeamManagementPage        = lazy(() => import('./pages/seller/TeamManagementPage'));
const VerificationCallback      = lazy(() => import('./pages/seller/VerificationCallback'));
const StripeCallback            = lazy(() => import('./pages/seller/stripe/StripeCallback'));
const StripeSetup               = lazy(() => import('./pages/seller/stripe/StripeSetup'));
const CreateBusiness            = lazy(() => import('./pages/seller/CreateBusiness'));
const SellerBillingPage         = lazy(() => import('./pages/seller/SellerBillingPage'));

// ---- Admin (lazy) ----
const AdminDashboardAnalysis= lazy(() => import('./pages/admin/AdminDashboardAnalysis'));
const AdminOrderListModern  = lazy(() => import('./pages/admin/AdminOrderListModern'));
// const AdminOrderDetails     = lazy(() => import('./pages/admin/AdminOrderDetails'));
const AdminOrderDetailsModern = lazy(() => import('./pages/admin/AdminOrderDetailsModern'));
const AdminUserList         = lazy(() => import('./pages/admin/user/AdminUserListModern'));
const AdminUserEdit         = lazy(() => import('./pages/admin/user/AdminUserEdit'));
const AdminBusinessList     = lazy(() => import('./pages/admin/business/AdminBusinessListModern'));
const AdminMarketplaceListings = lazy(() => import('./pages/admin/marketplace/AdminMarketplaceListings'));
const AdminProductListModern = lazy(() => import('./pages/admin/product/AdminProductListModern'));
const DeletedProductsModern = lazy(() => import('./pages/admin/product/DeletedProductsModern'));
const ProductCreate         = lazy(() => import('./pages/admin/product/ProductCreate'));
const ProductUpdate         = lazy(() => import('./pages/admin/product/ProductUpdate'));
const CreateCouponPage      = lazy(() => import('./pages/admin/coupon/CreateCouponPage'));
const AdminSellersPodcastsModern = lazy(() => import('./pages/admin/video/AdminSellersPodcastsModern'));
const AdminPodcastsModern   = lazy(() => import('./pages/admin/video/AdminPodcastsModern'));
const AdminCreatePodcastModern = lazy(() => import('./pages/admin/video/AdminCreatePodcastModern'));
const AdminPodcastUpdate    = lazy(() => import('./pages/admin/video/AdminPodcastUpdate'));
const HomepageManager       = lazy(() => import('./pages/admin/homepage/HomepageManager'));
const PromotionalSellerManager = lazy(() => import('./pages/admin/promotional/PromotionalSellerManager'));
const CollectionCreate      = lazy(() => import('./pages/admin/collection/CollectionCreate'));
const CollectionUpdate      = lazy(() => import('./pages/admin/collection/CollectionUpdate'));

// ---- Admin V2 Optimized Categories ----
const CategoryOptimizedCreate = lazy(() => import('./pages/admin/category-v2/CategoryOptimizedCreate'));
const CategoryOptimizedUpdate = lazy(() => import('./pages/admin/category-v2/CategoryOptimizedUpdate'));
const SubOptimizedCreate = lazy(() => import('./pages/admin/category-v2/SubOptimizedCreate'));
const SubOptimizedUpdate = lazy(() => import('./pages/admin/category-v2/SubOptimizedUpdate'));
const SubSubOptimizedCreate = lazy(() => import('./pages/admin/category-v2/SubSubOptimizedCreate'));
const SubSubOptimizedUpdate = lazy(() => import('./pages/admin/category-v2/SubSubOptimizedUpdate'));
const SubSubSubOptimizedCreate = lazy(() => import('./pages/admin/category-v2/SubSubSubOptimizedCreate'));
const SubSubSubOptimizedUpdate = lazy(() => import('./pages/admin/category-v2/SubSubSubOptimizedUpdate'));
const CategoryBulkImport = lazy(() => import('./pages/admin/category-v2/CategoryBulkImport'));
const CategoryBrowser = lazy(() => import('./pages/admin/category-v2/CategoryBrowser'));

// ---- Public pages (lazy) ----
const Home            = lazy(() => import('./pages/Home'));
const HomeOptimized   = lazy(() => import('./pages/HomeOptimized'));
const Mall            = lazy(() => import('./pages/Mall'));
const PlatformHub     = lazy(() => import('./pages/PlatformHub'));
const PlatformHubCategory = lazy(() => import('./pages/PlatformHubCategory'));
const PlatformPodcast = lazy(() => import('./pages/PlatformPodcast'));
const VirtualMallPrototypePage = lazy(() => import('./pages/VirtualMallPage'));
const VirtualMallCorridorPage = lazy(() => import('./pages/VirtualMallCorridorPage'));
const Shop            = lazy(() => import('./pages/Shop'));
const Marketplace     = lazy(() => import('./pages/Marketplace'));
const UsedProductDetail = lazy(() => import('./pages/UsedProductDetail'));
const MySpace         = lazy(() => import('./pages/MySpace'));
const PodcastsPage    = lazy(() => import('./pages/PodcastsPage'));
const Messages        = lazy(() => import('./pages/Messages'));
const CreateListing   = lazy(() => import('./pages/user/CreateListing'));
const EditListing     = lazy(() => import('./pages/user/EditListing'));
// const Videos          = lazy(() => import('./pages/Videos'));
const VideosModern    = lazy(() => import('./pages/VideosModern'));
// const Cart            = lazy(() => import('./pages/Cart'));
const CartModern      = lazy(() => import('./pages/CartModern'));
const Checkout        = lazy(() => import('./pages/Checkout'));
const Product         = lazy(() => import('./pages/Product'));
const ProductArtistic = lazy(() => import('./pages/ProductArtistic'));
const SingleVideoPage = lazy(() => import('./pages/SingleVideoPage'));

// LEGACY - commented out for migration testing
// const CategoryHome    = lazy(() => import('./pages/category/CategoryHome'));
const CategoryPage    = lazy(() => import('./pages/CategoryPage'));
const SubcategoryPage = lazy(() => import('./pages/SubcategoryPage'));
const CategoryProductsPage = lazy(() => import('./pages/CategoryProductsPage'));
const CategoryGlassGalleryPage = lazy(() => import('./pages/CategoryGlassGalleryPage'));
const KidsLandingPage = lazy(() => import('./pages/KidsLandingPage'));
const CollectionPage = lazy(() => import('./pages/collection/CollectionPage'));
const CollectionsIndex = lazy(() => import('./pages/collection/CollectionsIndex'));
const PaymentModern   = lazy(() => import('./pages/PaymentModern'));
const CanceledPayment = lazy(() => import('./pages/CanceledPayment'));
// const CustomerOrderDetails = lazy(() => import('./pages/user/CustomerOrderDetails'));
const CustomerOrderDetailsModern = lazy(() => import('./pages/user/CustomerOrderDetailsModern'));
// const Profile         = lazy(() => import('./pages/user/Profile'));
const BecomeSeller    = lazy(() => import('./pages/user/BecomeSeller'));
// const History         = lazy(() => import('./pages/user/History'));
const HistoryModern   = lazy(() => import('./pages/user/HistoryModern'));
const PdfDisplay      = lazy(() => import('./pages/user/PdfDisplay'));
const Wishlist        = lazy(() => import('./pages/user/Wishlist'));
const WishlistModern  = lazy(() => import('./pages/user/WishlistModern'));
const ProfileModern   = lazy(() => import('./pages/user/ProfileModern'));
const ReferralDashboard = lazy(() => import('./pages/user/ReferralDashboard'));
const PrivacySettings = lazy(() => import('./pages/user/PrivacySettings'));
const TwoFactorAuthPage = lazy(() => import('./pages/user/TwoFactorAuthPage'));
const CreateClip = lazy(() => import('./pages/user/CreateClip'));
const EditClip = lazy(() => import('./pages/user/EditClip'));
const DigitalLibrary = lazy(() => import('./pages/user/DigitalLibrary'));

// ---- Admin Referral ----
const ReferralSettings = lazy(() => import('./pages/admin/ReferralSettings'));

// ---- Admin Settings ----
const AdminContactSettings = lazy(() => import('./pages/admin/AdminContactSettings'));

// ---- Admin Landing Pages ----
const LandingPageList = lazy(() => import('./pages/admin/landing-page/LandingPageList'));
const LandingPageEditor = lazy(() => import('./pages/admin/landing-page/LandingPageEditor'));

// ---- Admin Design Requests ----
const AdminDesignRequests = lazy(() => import('./pages/admin/AdminDesignRequests'));

// ---- Admin Contact Submissions ----
const AdminContactSubmissions = lazy(() => import('./pages/admin/AdminContactSubmissions'));

// ---- Admin Verifications ----
const AdminVerifications = lazy(() => import('./pages/admin/AdminVerifications'));

// ---- Admin Seller Promotions ----
const AdminSellerPromotions = lazy(() => import('./pages/admin/seller-promotions/AdminSellerPromotions'));

// ---- Admin Legal Agreements ----
const AdminAgreements = lazy(() => import('./pages/admin/agreements/AdminAgreements'));

// ---- Public Landing Page ----
const LandingPage = lazy(() => import('./pages/landing/LandingPage'));

// ---- Public Contact Page ----
const ContactPage = lazy(() => import('./pages/ContactPage'));

// ---- Public Sell Page ----
const Sell = lazy(() => import('./pages/Sell'));


const App = () => {
  const { data: user, authChecked } = useSelector((state) => state.user);
  const dispatch = useDispatch();

  useEffect(() => {
    let tokenRefreshInterval = null;

    const refreshToken = async (firebaseUser) => {
      if (!firebaseUser) return null;
      try {
        // Force refresh the token
        const idTokenResult = await firebaseUser.getIdTokenResult(true);
        return idTokenResult.token;
      } catch (error) {
        console.error('Token refresh failed:', error);
        return null;
      }
    };

    const updateUserState = async (firebaseUser, token) => {
      try {
        const res = await currentUser(token);
        dispatch({
          type: 'LOGGED_IN_USER',
          payload: {
            name: res.data.name,
            email: res.data.email,
            token: token,
            role: res.data.role,
            isSeller: res.data.isSeller,
            isAdmin: res.data.isAdmin,
            _id: res.data._id,
            images: res.data.images,
            picture: res.data.picture,
            username: res.data.username,
            businesses: res.data.businesses || [], // For team member access
          },
        });
      } catch (err) {
        console.error('Error updating user state:', err);
        // Important: Still mark auth as checked so routes can redirect to login
        // instead of being stuck in a loading state
        dispatch({ type: 'AUTH_CHECKED' });
      }
    };

    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      // Clear any existing refresh interval
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
        tokenRefreshInterval = null;
      }

      if (!u) {
        dispatch({ type: 'AUTH_CHECKED' });
        return;
      }

      const idTokenResult = await u.getIdTokenResult();
      await updateUserState(u, idTokenResult.token);
      // authChecked is set automatically by LOGGED_IN_USER action

      // Set up token refresh every 50 minutes (tokens expire after 60 minutes)
      tokenRefreshInterval = setInterval(async () => {
        const newToken = await refreshToken(u);
        if (newToken) {
          dispatch({
            type: 'REFRESH_TOKEN',
            payload: { token: newToken },
          });
        }
      }, 50 * 60 * 1000); // 50 minutes
    });

    return () => {
      unsubscribe();
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
      }
    };
  }, [dispatch]);

  return (
    <LoadingProvider>
      <BuyerLocationProvider>
      <RouteLoadingBar />
      <Suspense fallback={
        <LoadingTrigger>
          <div className="page-loading">
            <div className="page-loading__spinner"></div>
            <span className="page-loading__text">Loading</span>
          </div>
        </LoadingTrigger>
      }>
        {/* <HeaderProvider> */}
          <Routes>
          {/* <Route element={<MainLayout />}> */}
            {/* Auth */}
            <Route path="/login" element={<LoginModern />} />
            {/* LEGACY - commented out for migration testing */}
            {/* <Route path="/login-legacy" element={<Login />} /> */}
            <Route path="/register" element={<RegisterModern />} />
            {/* <Route path="/register-legacy" element={<Register />} /> */}
            <Route path="/forgot/password" element={<ForgotPasswordModern />} />
            {/* <Route path="/forgot/password-legacy" element={<ForgotPassword />} /> */}
            <Route path="/user/management" element={<UserManagementModern />} />
            {/* <Route path="/user/management-legacy" element={<UserManagement />} /> */}

            {/* Admin */}
            <Route
              path="/admin/dashboardanalysis"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminDashboardAnalysis />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/orderlist/:id"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminOrderDetailsModern />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/orderlist"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminOrderListModern />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/userlist"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminUserList />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/user/:id/edit"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminUserEdit />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/businesses"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminBusinessList />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/marketplace-listings"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminMarketplaceListings />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/products"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminProductListModern />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/products/deleted"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <DeletedProductsModern />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/productupdate/:slug"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <ProductUpdate />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/product"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <ProductCreate />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/collections"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <CollectionCreate />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/collections/:id"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <CollectionUpdate />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/podcasts"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminPodcastsModern />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/sellers-podcasts"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminSellersPodcastsModern />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/podcast"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminCreatePodcastModern />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/podcast-update/:slug"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminPodcastUpdate />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/coupon"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <CreateCouponPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/homepage-sections"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <HomepageManager />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/promotional-sellers"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <PromotionalSellerManager />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/referral-settings"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <ReferralSettings />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/contact-settings"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminContactSettings />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/design-requests"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminDesignRequests />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/contact-submissions"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminContactSubmissions />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/verifications"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminVerifications />
                </AdminRoute>
              }
            />
            {/* Admin Site Banners */}
            {/* Admin Seller Promotions */}
            <Route
              path="/admin/seller-promotions"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminSellerPromotions />
                </AdminRoute>
              }
            />
            {/* Admin Legal Agreements */}
            <Route
              path="/admin/agreements"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <AdminAgreements />
                </AdminRoute>
              }
            />
            {/* Admin Landing Pages */}
            <Route
              path="/admin/landing-pages"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <LandingPageList />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/landing-pages/create"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <LandingPageEditor />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/landing-pages/:id"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <LandingPageEditor />
                </AdminRoute>
              }
            />
            {/* Admin V2 Optimized Categories */}
            <Route
              path="/admin/category-v2"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <CategoryOptimizedCreate />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/category-v2/:slug"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <CategoryOptimizedUpdate />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/sub-v2"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <SubOptimizedCreate />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/sub-v2/:slug"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <SubOptimizedUpdate />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/subsub-v2"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <SubSubOptimizedCreate />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/subsub-v2/:slug"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <SubSubOptimizedUpdate />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/subsubsub-v2"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <SubSubSubOptimizedCreate />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/subsubsub-v2/:slug"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <SubSubSubOptimizedUpdate />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/category-bulk-import"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <CategoryBulkImport />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/category-browser"
              element={
                <AdminRoute user={user} authChecked={authChecked}>
                  <CategoryBrowser />
                </AdminRoute>
              }
            />

            {/* Seller public routes (no persistent nav) */}
            <Route path="/:domain/videos/:id" element={<SingleVideoPage />} />
            <Route path="/:domain/podcast/:slug/:id" element={<SellerPodcast />} />
            <Route path="/:domain/customers-pictures/:sellerId" element={<SellerCustomersPictures />} />
            <Route path="/:domain/pressrelease/:id" element={<SellerPressRelease />} />
            <Route path="/:domain/home" element={<SellerHomePage />} />
            <Route path="/:domain/profile/:id" element={<SellerProfile />} />
            <Route path="/stripe/callback" element={<StripeCallback />} />
            <Route
              path="/seller/stripe-setup"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <StripeSetup />
                </UserRoute>
              }
            />

            {/* Seller protected routes */}
            <Route
              path="/seller/home-builder"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerHomeBuilderPage />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/home-preview"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerHomePreviewPage />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/create-podcast"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerPodcastCreateModern />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/update-podcast/:slug"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerPodcastUpdate />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/podcasts"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerPodcastsModern />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/productlist"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerProductListModern />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/productcreate"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerProductCreate />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/productupdate/:slug"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerProductUpdate />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/dashboard"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerDashboardModern />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/profile"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerProfilePage />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/orderlist"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerOrderListModern />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/orderlist/:id"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerOrderDetailsModern />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/press-release-coupon"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerPressReleaseCouponModern />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/verification"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerVerificationPage />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/verification/complete"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <VerificationCallback />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/location"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerLocationSettings />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/new-business"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <NewBusinessPage />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/team"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <TeamManagementPage />
                </SellerRoute>
              }
            />
            <Route
              path="/seller/billing"
              element={
                <SellerRoute user={user} authChecked={authChecked}>
                  <SellerBillingPage />
                </SellerRoute>
              }
            />

            {/* User protected */}
            {/* LEGACY - commented out for migration testing
            <Route
              path="/user/profile-legacy"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <Profile />
                </UserRoute>
              }
            /> */}
            <Route
              path="/user/become-seller"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <BecomeSeller />
                </UserRoute>
              }
            />
            <Route
              path="/seller/create-business"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <CreateBusiness />
                </UserRoute>
              }
            />
            <Route
              path="/user/history"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <HistoryModern />
                </UserRoute>
              }
            />
            {/* LEGACY - commented out for migration testing
            <Route
              path="/user/history-legacy"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <History />
                </UserRoute>
              }
            /> */}
            <Route
              path="/user/pdf-display/:id"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <PdfDisplay />
                </UserRoute>
              }
            />
            <Route
              path="/user/orderlist/:id"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <CustomerOrderDetailsModern />
                </UserRoute>
              }
            />
            {/* LEGACY - commented out for migration testing
            <Route
              path="/user/orderlist-legacy/:id"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <CustomerOrderDetails />
                </UserRoute>
              }
            /> */}
            <Route
              path="/user/wishlist"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <WishlistModern />
                </UserRoute>
              }
            />
            <Route
              path="/user/profile"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <ProfileModern />
                </UserRoute>
              }
            />
            <Route
              path="/user/referrals"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <ReferralDashboard />
                </UserRoute>
              }
            />
            <Route
              path="/user/privacy-settings"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <PrivacySettings />
                </UserRoute>
              }
            />
            <Route
              path="/user/security"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <TwoFactorAuthPage />
                </UserRoute>
              }
            />
            <Route
              path="/user/create-clip"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <CreateClip />
                </UserRoute>
              }
            />
            <Route
              path="/user/edit-clip/:clipId"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <EditClip />
                </UserRoute>
              }
            />
            <Route
              path="/user/digital-library"
              element={
                <UserRoute user={user} authChecked={authChecked}>
                  <DigitalLibrary />
                </UserRoute>
              }
            />

            {/* Commerce */}
            <Route path="/payment/:id" element={<PaymentModern />} />
            <Route path="/canceled" element={<CanceledPayment />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/marketplace/create" element={<CreateListing />} />
            <Route path="/marketplace/edit/:slug" element={<EditListing />} />
            <Route path="/marketplace/:slug" element={<UsedProductDetail />} />
            {/* My Space - Public User Profiles */}
            {/* More specific routes must come first */}
            <Route path="/my-space/user/:userId/clips/:clipId" element={<MySpace />} />
            <Route path="/my-space/:username/clips/:clipId" element={<MySpace />} />
            <Route path="/my-space/user/:userId" element={<MySpace />} />
            <Route path="/my-space/:username" element={<MySpace />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/messages/:conversationId" element={<Messages />} />
            <Route path="/videos" element={<VideosModern />} />
            <Route path="/clips" element={<Navigate to="/podcasts" replace />} />
            <Route path="/podcasts" element={<PodcastsPage />} />
            <Route path="/kids" element={<KidsLandingPage />} />
            <Route path="/cart" element={<CartModern />} />
            {/* <Route path="/cart-legacy" element={<Cart />} /> */}
            <Route path="/category/:categorySlug/:subSlug/:subSubSlug" element={<CategoryGlassGalleryPage />} />
            <Route path="/category/:categorySlug/:subSlug" element={<SubcategoryPage />} />
            <Route path="/category/:categorySlug" element={<CategoryPage />} />
            {/* LEGACY - commented out for migration testing */}
            {/* <Route path="/category/:name/:slug" element={<CategoryHome />} /> */}
            <Route path="/collections" element={<CollectionsIndex />} />
            <Route path="/collections/:slug" element={<CollectionPage />} />
            <Route path="/landing/:slug" element={<LandingPage />} />
            <Route path="/:domain/product/:slug" element={<Product />} />
            <Route path="/:domain/product-artistic/:slug" element={<ProductArtistic />} />
            <Route path="/:domain/video/:slug" element={<SingleVideoPage />} />

            {/* Public */}
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/sell" element={<Sell />} />
            <Route path="/mall" element={<Mall />} />
            <Route path="/virtual-mall" element={<VirtualMallCorridorPage />} />
            <Route path="/virtual-mall-prototype" element={<VirtualMallPrototypePage />} />
            {/* Platform Hub - Official Content */}
            <Route path="/platform-hub" element={<PlatformHub />} />
            <Route path="/platform-hub/podcast/:slug" element={<PlatformPodcast />} />
            <Route path="/platform-hub/:contentType" element={<PlatformHubCategory />} />
            <Route path="/home-v2" element={<HomeOptimized />} />
            <Route path="/" element={<Home />} />
          {/* </Route> */}
        </Routes>
      {/* </HeaderProvider> */}
      <InstallPWA />
      <AgreementChecker />
    </Suspense>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
      </BuyerLocationProvider>
    </LoadingProvider>
  );
};

export default App;
